import React from 'react';
import { Alert, Badge, Button, Form, Row, Col, Table } from 'react-bootstrap';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';
import FileUploader from '../../file/FileUploader';
import MASpinner from '../../common/MASpinner';
import { niceDateFormatter, niceDateTimeFormatter } from '../../../lib/helper/formatting';
import { downloadCcsdImportReport } from '../../../lib/helper/downloadHelper';

const STEPS = { UPLOAD: 'upload', PREVIEW: 'preview', RESULT: 'result' };

function extractApiError(err, fallback = 'An error occurred. Please try again.') {
  return err?.response?.body?.['hydra:description'] || err?.response?.body?.detail || fallback;
}

function getInitialState() {
  return {
    step: STEPS.UPLOAD,
    selectedFile: null,
    effectiveDate: '',
    isUploading: false,
    temporaryFileId: null,
    previewData: null,
    commitData: null,
    error: null,
    history: [],
    isLoadingHistory: false,
  };
}

class UploadFeeScheduleTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = getInitialState();
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchHistory();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchHistory() {
    const { healthInsurer } = this.props;
    if (!healthInsurer) return;

    this.setState({ isLoadingHistory: true });

    requests
      .get(API_ROUTES.CCSD_CODE_PRICE_IMPORTS, {
        healthInsurer: healthInsurer.id,
        'order[createdAt]': 'DESC',
        itemsPerPage: 20,
      })
      .then((response) => {
        if (!this._isMounted) return;
        this.setState({
          history: response['hydra:member'] || [],
          isLoadingHistory: false,
        });
      })
      .catch(() => {
        if (!this._isMounted) return;
        this.setState({ isLoadingHistory: false });
      });
  }

  handleFileAdded(e) {
    const file = e.target.files[0] || null;
    this.setState({ selectedFile: file, error: null });
  }

  handlePreview() {
    const { selectedFile, effectiveDate } = this.state;
    const { healthInsurer } = this.props;

    if (!selectedFile) {
      this.setState({ error: 'Please select a CSV file to upload.' });
      return;
    }
    if (!effectiveDate) {
      this.setState({ error: 'Please select an effective date.' });
      return;
    }

    this.setState({ isUploading: true, error: null });

    requests
      .upload(API_ROUTES.TEMPORARY_CCSD_IMPORT_FILES, selectedFile)
      .then((uploadResponse) => {
        if (!this._isMounted) return;
        const temporaryFileId = uploadResponse.id;
        this.setState({ temporaryFileId });

        return requests.post(API_ROUTES.CCSD_CODE_PRICE_IMPORTS, {
          temporaryFileId,
          effectiveDate,
          insurerId: healthInsurer.id,
          dryRun: true,
        });
      })
      .then((previewData) => {
        if (!this._isMounted || !previewData) return;
        this.setState({ isUploading: false, previewData, step: STEPS.PREVIEW });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, error: extractApiError(err, 'An error occurred while uploading the file. Please try again.') });
      });
  }

  handleConfirm() {
    const { temporaryFileId, effectiveDate } = this.state;
    const { healthInsurer } = this.props;

    this.setState({ isUploading: true, error: null });

    requests
      .post(API_ROUTES.CCSD_CODE_PRICE_IMPORTS, {
        temporaryFileId,
        effectiveDate,
        insurerId: healthInsurer.id,
        dryRun: false,
      })
      .then((commitData) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, commitData, step: STEPS.RESULT });
        this.fetchHistory();
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ isUploading: false, error: extractApiError(err, 'An error occurred during import. Please try again.') });
      });
  }

  handleBack() {
    this.setState({ step: STEPS.UPLOAD, error: null, previewData: null });
  }

  handleReset() {
    this.setState(getInitialState(), () => this.fetchHistory());
  }

  renderUploadStep() {
    const { selectedFile, effectiveDate, isUploading, error } = this.state;

    return (
      <>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3">
          <Col sm={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label><strong>Fee Schedule CSV</strong></Form.Label>
              <FileUploader
                accept=".csv,text/csv"
                multiple={false}
                onFilesAdded={this.handleFileAdded.bind(this)}
                requestInProgress={isUploading}
                title="Click here to select a CSV file"
                sizeLimitMB={10}
              />
              {selectedFile && (
                <small className="text-muted">Selected: {selectedFile.name}</small>
              )}
            </Form.Group>
          </Col>
          <Col sm={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label><strong>Prices effective from</strong></Form.Label>
              <Form.Control
                type="date"
                value={effectiveDate}
                onChange={(e) => this.setState({ effectiveDate: e.target.value })}
                disabled={isUploading}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="mb-3">
          <Button
            variant="primary"
            onClick={this.handlePreview.bind(this)}
            disabled={isUploading || !selectedFile || !effectiveDate}
            className="mr-2"
          >
            {isUploading ? 'Uploading...' : 'Preview Import'}
          </Button>
          <a href="/example-ccsd-import.csv" download className="btn btn-outline-secondary btn-sm">
            <i className="fas fa-download mr-1" />
            Download example CSV
          </a>
        </div>

        {this.renderHistory()}
      </>
    );
  }

  renderPreviewStep() {
    const { previewData, isUploading, error, effectiveDate } = this.state;
    const hasErrors = previewData?.errors?.length > 0;

    return (
      <>
        {error && <Alert variant="danger">{error}</Alert>}

        {hasErrors ? (
          <>
            <Alert variant="danger">
              <strong>Import cannot proceed — {previewData.errors.length} error{previewData.errors.length !== 1 ? 's' : ''} found in the file.</strong>
              <br />
              Please fix the issues below and re-upload.
            </Alert>
            <div style={{ maxHeight: '300px', overflow: 'auto' }} className="mb-3">
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Error</th></tr>
                </thead>
                <tbody>
                  {previewData.errors.map((err, i) => (
                    <tr key={i}><td className="text-danger">{err}</td></tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <Alert variant="warning">
            <strong>Preview — confirm to apply changes</strong>
            <br />
            Effective date: <strong>{niceDateFormatter(effectiveDate)}</strong>
            <br />
            Rows processed: <strong>{previewData?.rowsProcessed ?? 0}</strong>
            <br />
            New CCSD codes: <strong>{previewData?.newCodesCount ?? 0}</strong>
            <br />
            Prices to update: <strong>{previewData?.pricesUpdatedCount ?? 0}</strong>
            <br />
            Prices unchanged (skipped): <strong>{previewData?.pricesSkippedCount ?? 0}</strong>
            {previewData?.reportFileName && (
              <>
                <br />
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mt-2"
                  onClick={() => downloadCcsdImportReport(previewData.reportFileName)}
                >
                  <i className="fas fa-download mr-1" />
                  Download preview report
                </Button>
              </>
            )}
          </Alert>
        )}

        <div className="mt-3">
          <Button variant="secondary" className="mr-2" onClick={this.handleBack.bind(this)} disabled={isUploading}>
            Back
          </Button>
          {!hasErrors && (
            <Button variant="success" onClick={this.handleConfirm.bind(this)} disabled={isUploading}>
              {isUploading ? 'Importing...' : 'Confirm & Import'}
            </Button>
          )}
        </div>
      </>
    );
  }

  renderResultStep() {
    const { commitData } = this.state;

    return (
      <>
        <Alert variant="success">
          <strong>Fee schedule imported successfully.</strong>
          <br />
          {commitData?.newCodesCount > 0 && <>{commitData.newCodesCount} new CCSD code{commitData.newCodesCount !== 1 ? 's' : ''} created. </>}
          {commitData?.pricesUpdatedCount > 0 && <>{commitData.pricesUpdatedCount} price{commitData.pricesUpdatedCount !== 1 ? 's' : ''} updated. </>}
        </Alert>
        <Button variant="primary" onClick={this.handleReset.bind(this)}>
          Import Another
        </Button>
      </>
    );
  }

  renderHistory() {
    const { history, isLoadingHistory } = this.state;

    if (isLoadingHistory) {
      return <MASpinner msg="Loading import history..." />;
    }

    if (history.length === 0) {
      return (
        <Alert variant="secondary" className="mt-4">
          No fee schedule imports have been made for this insurer yet.
        </Alert>
      );
    }

    return (
      <div className="mt-4">
        <h5>Previous Imports</h5>
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Uploaded</th>
              <th>Effective Date</th>
              <th>Rows in File</th>
              <th>New Codes</th>
              <th>Prices Updated</th>
              <th>Prices Unchanged</th>
              <th>Report</th>
            </tr>
          </thead>
          <tbody>
            {history.map((record) => (
              <tr key={record.id}>
                <td>{niceDateTimeFormatter(record.createdAt)}</td>
                <td>{niceDateFormatter(record.effectiveDate)}</td>
                <td>{record.rowsProcessed ?? 0}</td>
                <td>
                  <Badge variant={record.newCodesCount > 0 ? 'success' : 'secondary'}>
                    {record.newCodesCount}
                  </Badge>
                </td>
                <td>
                  <Badge variant={record.pricesUpdatedCount > 0 ? 'primary' : 'secondary'}>
                    {record.pricesUpdatedCount}
                  </Badge>
                </td>
                <td>
                  <Badge variant="secondary">
                    {record.pricesSkippedCount ?? 0}
                  </Badge>
                </td>
                <td>
                  {record.reportFileName ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => downloadCcsdImportReport(record.reportFileName)}
                    >
                      <i className="fas fa-download mr-1" />
                      Download
                    </Button>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <div>
        {step === STEPS.UPLOAD && this.renderUploadStep()}
        {step === STEPS.PREVIEW && this.renderPreviewStep()}
        {step === STEPS.RESULT && (
          <>
            {this.renderResultStep()}
            <div className="mt-4">
              {this.renderHistory()}
            </div>
          </>
        )}
      </div>
    );
  }
}

export default UploadFeeScheduleTab;
